import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGuard from "@/components/RoleGuard";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Resellers from "@/pages/Resellers";
import Pricing from "@/pages/Pricing";
import Wallets from "@/pages/Wallets";
import Campaigns from "@/pages/Campaigns";
import Reports from "@/pages/Reports";
import CDR from "@/pages/CDR";
import SystemSettings from "@/pages/SystemSettings";
import OpensipsServers from "@/pages/OpensipsServers";
import MediaServers from "@/pages/MediaServers";
import AIEngines from "@/pages/AIEngines";
import VicidialServers from "@/pages/VicidialServers";
import GlobalTemplates from "@/pages/GlobalTemplates";
import BillingPricing from "@/pages/BillingPricing";
import AdminPayments from "@/pages/AdminPayments";
import CallLimit from "@/pages/CallLimit";
import AccountBilling from "@/pages/AccountBilling";
import AccountPayments from "@/pages/AccountPayments";
import ResellerPricing from "@/pages/ResellerPricing";
import LiveCalls from "@/pages/LiveCalls";
import SystemLogs from "@/pages/SystemLogs";
import ImpersonateLanding from "@/pages/ImpersonateLanding";
import StatsReport from "@/pages/StatsReport";
import Landing from "@/pages/Landing";
import { useAuth } from "@/context/AuthContext";

function RootGate() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/impersonate" element={<ImpersonateLanding />} />
            <Route path="/" element={<RootGate />} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/customers" element={<RoleGuard allow={["admin", "reseller"]}><Customers /></RoleGuard>} />
              <Route path="/resellers" element={<RoleGuard allow={["admin"]}><Resellers /></RoleGuard>} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/live-calls" element={<LiveCalls />} />
              <Route path="/templates" element={<RoleGuard allow={["admin"]}><GlobalTemplates /></RoleGuard>} />
              <Route path="/servers/opensips" element={<RoleGuard allow={["admin"]}><OpensipsServers /></RoleGuard>} />
              <Route path="/servers/media" element={<RoleGuard allow={["admin"]}><MediaServers /></RoleGuard>} />
              <Route path="/servers/ai-engines" element={<RoleGuard allow={["admin"]}><AIEngines /></RoleGuard>} />
              <Route path="/servers/vicidial" element={<VicidialServers />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/billing/pricing" element={<RoleGuard allow={["admin"]}><BillingPricing /></RoleGuard>} />
              <Route path="/billing/payments" element={<RoleGuard allow={["admin"]}><AdminPayments /></RoleGuard>} />
              <Route path="/call-limit" element={<RoleGuard allow={["reseller", "customer"]}><CallLimit /></RoleGuard>} />
              <Route path="/billing" element={<RoleGuard allow={["reseller", "customer"]}><AccountBilling /></RoleGuard>} />
              <Route path="/payments" element={<RoleGuard allow={["reseller", "customer"]}><AccountPayments /></RoleGuard>} />
              <Route path="/reseller-pricing" element={<RoleGuard allow={["reseller"]}><ResellerPricing /></RoleGuard>} />
              <Route path="/wallet" element={<RoleGuard allow={["reseller", "customer"]}><Wallets /></RoleGuard>} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/reports/stats" element={<RoleGuard allow={["admin"]}><StatsReport /></RoleGuard>} />
              <Route path="/cdr" element={<CDR />} />
              <Route path="/settings" element={<RoleGuard allow={["admin"]}><SystemSettings /></RoleGuard>} />
              <Route path="/system-logs" element={<RoleGuard allow={["admin"]}><SystemLogs /></RoleGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
